import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useEffect, useRef, useState } from 'react';
import { Auto, ChipList, PastDateInline } from '../../core/controls';
import { DateInput, dateFromInput, formatLocalDate } from '../../core/date-time';
import { Expansion, ExpansionDetails, ExpansionSummary } from '../../core/expansion';
import { FormApi, formApi } from '../../core/form';
import { lowerCaseDesc } from '../../core/text-format';
import { VisitContext, WorksheetApis } from '../../core/visit-context';
import { addArrivalmodeRequest, addDispositionRequest, addInjuryRequest, deleteArrivalmodeRequest, deleteDispositionRequest, deleteInjuryRequest } from '../../core/visit-tracking';
import { HospitalistContext } from '../../core/hospitalist-context';
import { UsState, usStateLabel, usStates } from '../../core/us-states';

type TrackingForm = {
  mode: any;
  injuryDate: Date;
  injuryState?: UsState;
  modeAuto?: number;
};

function ModeSelection({ options, autoOptions, label, request, value, injuryDate, injuryState, readonly, onDelete, hospitalistContext, worksheetDate }) {
  hospitalistContext && hospitalistContext.data((s) => s.selectedId);

  async function onChange(values: TrackingForm, field: 'mode' | 'modeAuto' | 'injuryDate' | 'injuryState') {
    const v = field === 'mode' ? values.mode : mode.value.getState().value;
    let defaultInjuryDate: DateInput = values.injuryDate;
    if (label === 'Injury' && field === 'mode' && !values.injuryDate) {
      if (hospitalistContext) {
        const apis: WorksheetApis = hospitalistContext.selectedApis();
        if (apis) {
          defaultInjuryDate = apis.context.worksheet.serviceDate;
        }
      } else {
        defaultInjuryDate = worksheetDate;
      }

      if (defaultInjuryDate) {
        const value = dateFromInput(defaultInjuryDate);
        date.value.setState({ value });
        values.injuryDate = value;
        // keep the form's central value store in sync too, otherwise it still has the old
        // (null) injuryDate, and the next field change (e.g. selecting the state) reads that
        // stale value, fails validateInjury, and silently drops the request
        modes.values.getState().setValue({ ...modes.values.getState().value, injuryDate: value });
      }
    }
    const validateInjury = label !== 'Injury' || defaultInjuryDate;

    // the state control holds the current selection, the form values are only patched by the field being changed
    const selectedState: UsState = field === 'injuryState' ? values.injuryState : injuryStateControl?.value.getState().value;

    function clearModes() {
      onDelete();
      autoOptions.forEach((o: any) => (o.selected = false));
      modeAuto.options.setState({ value: autoOptions.slice() });
      modeAuto.value.setState({ value: null });
    }

    if (v?.code && validateInjury) {
      await request({ ...v, ...{ injuryDate: defaultInjuryDate, injuryState: selectedState?.code || null } });

      modeAuto.value.setState({ value: null });
      autoOptions.forEach((o: any) => {
        o.selected = false;

        if (v.code === o.value) {
          o.selected = true;
          modeAuto.value.setState({ value: o });
        }
      });
    } else {
      if (label !== 'Injury') {
        clearModes();
      } else if (!v?.code) {
        clearModes();
        if (values.injuryDate) {
          date.value.setState({ value: null });
        }
        if (selectedState) {
          injuryStateControl.value.setState({ value: null });
        }
      }
    }
    checkDesc();
  }

  const modes: FormApi<TrackingForm> = useRef(
    formApi<TrackingForm>({
      disabled: readonly,
      values: {
        mode: null,
        modeAuto: -1,
        injuryDate: injuryDate ? dateFromInput(injuryDate) : null,
        injuryState: null,
      },
      fields: {
        mode: {
          label: '',
          options,
          placeholder: 'Select Manually',
          list: options,
          listLabel: (v) => lowerCaseDesc(v.description),
          equals: (item: any, code: string) => item.code === code,
          onChange: (v) => {
            const values = { ...modes.values.getState().value, ...{ mode: v } };
            onChange(values, 'mode');
            if (label === 'Injury') {
              date.disabled.setState({ value: !v });
              injuryStateControl.disabled.setState({ value: !v });
            }
          },
        },
        modeAuto: {
          required: true,
          options: autoOptions,
          onChange: (e) => {
            if (e.selected) {
              mode.setListValue(e.value);
            } else {
              mode.setListValue(null);
            }
            checkDesc();
          },
        },
        injuryDate: {
          required: true,
          style: { width: '100%' },
          onChange: (v) => {
            onChange(
              {
                ...modes.values.getState().value,
                ...{ injuryDate: v },
              },
              'injuryDate'
            );
          },
        },
        injuryState: {
          label: '',
          placeholder: 'State',
          style: { width: '100%' },
          list: usStates,
          listLabel: usStateLabel,
          equals: (item: UsState, v: UsState | string) => item?.code === (typeof v === 'string' ? v : v?.code),
          onChange: (v: UsState) => {
            onChange(
              {
                ...modes.values.getState().value,
                ...{ injuryState: v },
              },
              'injuryState'
            );
          },
        },
      },
    })
  ).current;

  const { mode, modeAuto, injuryDate: date, injuryState: injuryStateControl } = modes.controls;

  const [desc, setDesc] = useState(null);
  function checkDesc() {
    const v = mode.value.getState().value;
    if (v) {
      setDesc(lowerCaseDesc(v.description));
    } else {
      setDesc(null);
    }
  }
  useEffect(() => {
    if (label === 'Injury' && injuryState) {
      // seed the saved state before setListValue below re-posts the injury, otherwise it would be sent as empty
      const saved = usStates.find((s) => s.code === injuryState);
      if (saved) {
        injuryStateControl.value.setState({ value: saved });
      }
    }

    if (value) {
      mode.setListValue(value);
      checkDesc();
      autoOptions.forEach((o: any) => {
        if (value === o.value) {
          o.selected = true;
        }
      });
    }

    if (label === 'Injury') {
      date.disabled.setState({ value: !value });
      injuryStateControl.disabled.setState({ value: readonly || !value });
    }

  }, []);

  const style = {
    width: '100%',
    paddingLeft: 8,
  };

  const [expanded, setExpanded] = useState(typeof value === 'number' ? !value : !value || !value.length);

  const onClicked = () => {
    setExpanded(!expanded);
  };

  return (
    <Expansion expanded={expanded} style={{ width: '100%' }}>
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon />}>
        <Grid container direction="row" wrap="nowrap" style={{ width: '100%' }} alignItems="center">
          <Grid item style={{ flexShrink: 0 }}>
            <Typography style={{ whiteSpace: 'nowrap', paddingLeft: 8 }} variant="body2" color="textSecondary">
              {label}
            </Typography>
          </Grid>
          <Grid item style={{ flexGrow: 1 }}></Grid>
        </Grid>
      </ExpansionSummary>
      {!expanded && desc && (
        <Typography
          color="textPrimary"
          style={{ paddingLeft: 8, paddingRight: 8, whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {desc}
        </Typography>
      )}
      <ExpansionDetails>
        <Grid container direction="column" style={style}>
          {label === 'Injury' ? (
            <>
              <Grid item style={{ width: '100%' }}>
                <Auto {...modes.controls.mode} />
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <PastDateInline {...date} />
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <Auto {...injuryStateControl} />
              </Grid>
            </>
          ) : (
            <Auto {...modes.controls.mode} />
          )}

          <div style={{ paddingTop: 4 }}>
            <ChipList {...modes.controls.modeAuto} />
          </div>
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

interface TrackingCodesProps {
  context: VisitContext;
  visitId?: string;
  arrival?: string;
  disposition?: string;
  injury?: string;
  injuryDate?: DateInput;
  injuryState?: string;
  readonly?: boolean;
}

type VisitArgs = {
  visitId: string;
  code: string;
  description: string;
  injuryDate?: Date; // TODO optional, based on config
  injuryState?: string;
  label?: string;
};
function visitRequest(visitId: string, request: (args: any) => void): (o: VisitArgs) => void {
  return async (o) => {
    await request({
      visitId,
      code: o.code || o.label,
      description: o.description,
      injuryDate: o.injuryDate ? formatLocalDate(o.injuryDate, 'yyyy-MM-dd') : new Date(),
      injuryState: o.injuryState,
    });
  };
}
export function VisitTrackingCodes(props: TrackingCodesProps) {
  const { context, visitId, arrival, disposition, injury, injuryDate, injuryState, readonly } = {
    ...{ arrival: null, disposition: null, injury: null, injuryState: null, visitId: null },
    ...props,
  } as VisitContext & TrackingCodesProps;

  const { config, hospitalistContext } = context;
  const { arrivalModeList, arrivalModeOptions, dispositionList, dispositionOptions, accidentList, accidentOptions } = config;

  //TODO normalize injury - code is string, option is integer
  const onDeleteArrival = async () => {
    await deleteArrivalmodeRequest({ visitId });
  };
  const onDeleteDisposition = async () => {
    await deleteDispositionRequest({ visitId });
  };

  const onDeleteInjury = async () => {
    await deleteInjuryRequest({ visitId });
  };

  let worksheetDate: DateInput = null;
  if (!hospitalistContext) {
    const apis = context.worksheets[0];
    if (apis) {
      worksheetDate = apis.context.worksheet.serviceDate;
    }
  }

  return (
    <Grid item container direction="column" style={{ paddingTop: 0, width: 324 }} spacing={2}>
      <ModeSelection
        label="Arrival Mode"
        readonly={readonly}
        options={arrivalModeList}
        autoOptions={(
          arrivalModeOptions || [
            { label: 'Private Transport', value: 'PRIV' },
            { label: 'Ambulance', value: 'AMB' },
          ]
        ).map((o) => ({ ...o, ...{ selected: o.value === arrival } }))}
        request={visitRequest(visitId, addArrivalmodeRequest)}
        value={arrival}
        injuryDate={null}
        injuryState={null}
        worksheetDate={worksheetDate}
        hospitalistContext={hospitalistContext}
        onDelete={onDeleteArrival}
      />

      <ModeSelection
        label="Disposition"
        readonly={readonly}
        options={dispositionList}
        autoOptions={(
          dispositionOptions || [
            { label: 'LWBS', value: 'LWBS' },
            { label: 'AMA', value: 'AMA' },
            { label: 'Home', value: 'HOME' },
            { label: 'Admitted', value: 'ADMIT' },
          ]
        ).map((o) => ({ ...o, ...{ selected: o.value === disposition } }))}
        request={visitRequest(visitId, addDispositionRequest)}
        value={disposition}
        injuryDate={null}
        injuryState={null}
        worksheetDate={worksheetDate}
        hospitalistContext={hospitalistContext}
        onDelete={onDeleteDisposition}
      />

      <ModeSelection
        label="Injury"
        readonly={readonly}
        options={accidentList}
        autoOptions={
          accidentOptions || [
            { label: 'Accident / Coverage', value: 1 },
            { label: 'Crime', value: 6 },
            { label: 'Auto Accident', value: 2 },
          ]
        }
        request={visitRequest(visitId, addInjuryRequest)}
        value={parseInt(injury)}
        injuryDate={injuryDate}
        injuryState={injuryState}
        hospitalistContext={hospitalistContext}
        worksheetDate={worksheetDate}
        onDelete={onDeleteInjury}
      />
    </Grid>
  );
}
